import {
  ButtonLogo,
  Media,
  MediaFooter,
  MediaOverlay,
  Sticker,
} from '@dce-front/dive';
import { CreativeMediaCard } from '@dce-front/onewebapp-creativemedia';
import type { ImageSize } from '@dce-front/onewebapp-dive-utils';
import { DIMENSIONS } from '@dce-front/onewebapp-dive-utils';
import { Ratio } from '@dce-front/onewebapp-utils';
import type { PersoLists } from '@dce-front/sdk-hodor';
import { Template, TitleDisplayMode } from '@dce-front/sdk-hodor';
import classNames from 'classnames/bind';
import { useMemo, type CSSProperties, type JSX } from 'react';
import { useSelector } from 'react-redux';
import { isOngoing } from '../../../../helpers/card/card-helper';
import { getContentAriaLabel } from '../../../../helpers/contents/contents-helper';
import { useInvariantSelector } from '../../../../helpers/hooks/useInvariantSelector';
import { getDiveMediaFooterFixedHeight } from '../../../../helpers/media/media-helpers';
import {
  getHodorSticker,
  useGetDiveStickerProps,
} from '../../../../helpers/stickers/stickers-helper';
import { useTranslation } from '../../../../lang';
import { featUpcomingHodorStickerSelector } from '../../../../store/slices/application-selectors';
import { imageQualityPercentageBySettingsSelector } from '../../../../store/slices/user-selectors';
import type { ContentStrateV5 } from '../../../../templates/LandingV5/data/formatter';
import { CardRemoveButton } from '../../../CardContents/CardRemoveButton/CardRemoveButton';
import Linker from '../../../Linker/Linker';
import type { LinkerData } from '../../../Linker/types';
import { useRoutingContext } from '../../../Page/RoutingContext';
import { useIsFrom } from '../../../Page/useIsFrom';
import DiveProgressBar from '../../../ProgressBar/DiveProgressBar';
import ThumborMediaImage from '../../../ThumborMediaImage/ThumborMediaImage';
import styles from '../ContentRowTemplateItem.css';
import { RankItemStandard } from './RankItemStandard';

const cx = classNames.bind(styles);

export type ContentRowTemplateItemStandardProps = {
  content: ContentStrateV5;
  imageSize?: ImageSize;
  isBackgroundTransparent?: boolean;
  isRemovableItem?: boolean;
  listType?: PersoLists;
  onClickRemove?: (
    event: React.MouseEvent,
    contentID: string,
    listType: PersoLists,
  ) => void;
  ratio: Ratio;
  titleDisplayMode?: TitleDisplayMode;
  isTop10?: boolean;
  style?: CSSProperties;
  index?: number;
};

/**
 * Generates a label for a content item, optionally including its Top 10 ranking
 *
 * @returns The formatted label string, prefixed with "Top X" if isTop10 is true
 *
 * @example
 *
 * ```typescript
 * getTop10Label("Movie Title", { rank: 3 }, true) // "Top 3 Movie Title"
 * getTop10Label("TV Show", { rank: 1 }, true) // "Top 1 TV Show"
 * getTop10Label("Regular Content", { rank: 5 }, false) // "Regular Content"
 * ```
 */
const getTop10Label = (
  label: string | undefined,
  content: ContentStrateV5,
  isTop10: boolean,
) => (isTop10 ? `Top ${content.rank} ${label}` : label);

export function ContentRowTemplateItemStandard({
  content,
  titleDisplayMode = TitleDisplayMode.All,
  ratio,
  imageSize = 'normal',
  isBackgroundTransparent = false,
  isRemovableItem = false,
  isTop10 = false,
  listType,
  style,
  index,
  onClickRemove,
}: ContentRowTemplateItemStandardProps): JSX.Element | null {
  const { t } = useTranslation();

  const isFeatUpcomingHodorSticker = useInvariantSelector(
    featUpcomingHodorStickerSelector,
  );
  const isFromLanding = useIsFrom(Template.Landing);
  const isFromDetail = useIsFrom(Template.DetailPage);
  const routingContext = useRoutingContext();
  const isFromImmersive = routingContext === 'immersive' && !isFromLanding;
  const qualityUserSettings = useSelector(
    imageQualityPercentageBySettingsSelector,
  );

  const linkerData = useMemo(
    () =>
      ({
        contentID: content?.contentID,
        mainOnClick: content?.onClick,
        context: content?.context,
      }) satisfies LinkerData,
    [content],
  );

  const { startTime, endTime } = content;

  const isLive = isOngoing({ startTime, endTime });

  const topStickerProps = useGetDiveStickerProps({
    sticker: getHodorSticker({
      stickers: content.stickers,
      isFeatUpcomingHodorSticker,
    }),
    startTime: startTime,
    endTime: endTime,
    showLiveSticker: !content.userProgress && isLive && content.isInOffer,
  });

  const bottomStickerProps = useGetDiveStickerProps({
    sticker: getHodorSticker({
      stickers: content.stickers,
      isFeatUpcomingHodorSticker,
      position: 'bottom',
    }),
    position: 'bottom',
  });

  const isDescriptionVisible =
    titleDisplayMode === TitleDisplayMode.Description ||
    titleDisplayMode === TitleDisplayMode.All;

  const URLImage = content.URLImage;

  const { URLImageForDarkMode = URLImage, imageSpecificities } = content;

  const isTitleVisible = titleDisplayMode !== TitleDisplayMode.None;
  const title = isTitleVisible ? content.title : '';
  const subtitle =
    isTitleVisible && titleDisplayMode !== TitleDisplayMode.TitleOnly
      ? content.subtitle
      : '';
  const description = isDescriptionVisible ? content.description : '';
  const image = {
    default: URLImageForDarkMode,
  };

  const isLogoChannel = imageSpecificities === 'isLogo';

  const isCreativeMedia = content.type === Template.CreativeMedia;
  const isBottomTitle = !(
    [TitleDisplayMode.LogoTyped, TitleDisplayMode.None] as TitleDisplayMode[]
  ).includes(titleDisplayMode);
  const isClickableCard = !!content.onClick?.URLPage;

  const linkerTitle = getTop10Label(content.title, content, isTop10);

  const ariaLabel = useMemo(() => {
    const value = getContentAriaLabel(content, {
      sticker: topStickerProps?.label,
    });
    return getTop10Label(value, content, isTop10);
  }, [content, topStickerProps?.label, isTop10]);

  const dimensions = (DIMENSIONS as any)[ratio][imageSize];

  const progressBar = content.userProgress ? (
    <DiveProgressBar progress={content.userProgress} showIconOnComplete />
  ) : isLive ? (
    <DiveProgressBar startTime={startTime} endTime={endTime} isLive />
  ) : undefined;

  const shouldDisplayTitle =
    TitleDisplayMode.LogoTyped && !content.isLogoTyped && !isBottomTitle;

  // MediaOverlay can not display progressBar and stickerBottom
  const getProgressBarOrStickerBottomProps = () => {
    if (progressBar) {
      return { progressBar };
    }
    if (bottomStickerProps) {
      return {
        stickerBottom: <Sticker {...bottomStickerProps} />,
      };
    }
    return undefined;
  };

  const mediaCard = (
    <Media
      aspectRatio={ratio}
      aria-label={ariaLabel}
      image={
        <ThumborMediaImage
          dimensions={dimensions}
          url={URLImage}
          alt={content.altImage}
        />
      }
      variant={isBackgroundTransparent ? 'ghost' : 'solid'}
      overlay={
        <MediaOverlay
          actionableElementTop={
            isRemovableItem && onClickRemove && !$_BUILD_RENDERMODE_CSR ? (
              // This component will be remove when it will be refactored dive side
              <CardRemoveButton
                contentID={content.contentID as string}
                listType={listType}
                onClickRemove={onClickRemove}
                hasSticker={Boolean(topStickerProps?.variant)}
              />
            ) : undefined
          }
          logo={
            content.URLLogoChannel ? (
              <ThumborMediaImage
                dimensions={
                  $_BUILD_RENDERMODE_CSR
                    ? DIMENSIONS.LOGO_CHANNEL.large
                    : DIMENSIONS.LOGO_CHANNEL.normal
                }
                url={content.URLLogoChannel}
                alt={content.altLogoChannel}
              />
            ) : undefined
          }
          title={shouldDisplayTitle ? title : undefined}
          subtitle={!shouldDisplayTitle ? subtitle : undefined}
          stickerTop={
            topStickerProps ? <Sticker {...topStickerProps} /> : undefined
          }
          {...getProgressBarOrStickerBottomProps()}
        />
      }
      footer={
        isBottomTitle ? (
          <MediaFooter
            title={title}
            subtitle={subtitle}
            description={description}
            fixedHeight={getDiveMediaFooterFixedHeight({
              titleDisplayMode,
              hasDescription: !!description,
            })}
          />
        ) : undefined
      }
      data-tv-focusable={!isClickableCard}
      className={cx('contentRowTemplateItem__itemLink', {
        'contentRowTemplateItem__itemLink--top10__media': isTop10,
      })}
    />
  );

  // Clickable card
  if (isClickableCard) {
    const itemClassnames = cx('contentRowTemplateItem', {
      'contentRowTemplateItem--carousel':
        ratio === Ratio.Ratio166 && !isDescriptionVisible,
      'contentRowTemplateItem--immersive': isFromImmersive,
      'contentRowTemplateItem--logoTyped':
        titleDisplayMode === TitleDisplayMode.LogoTyped,
      'contentRowTemplateItem--logoTyped--top10':
        titleDisplayMode === TitleDisplayMode.LogoTyped && isTop10,
      'contentRowTemplateItem--detailV5': isFromDetail,
      'contentRowTemplateItem--top10': isTop10,
      'contentRowTemplateItem--creativeMedia': isCreativeMedia,
      'contentRowTemplateItem--creativeMediaInDetailV5':
        isCreativeMedia && isFromDetail,
      'contentRowTemplateItem--isBottomTitle': isBottomTitle,
    });

    return (
      <li className={itemClassnames} style={style} data-index={index}>
        <Linker
          className={cx('contentRowTemplateItem__itemLink', {
            'contentRowTemplateItem__itemLink--top10': isTop10,
          })}
          data={linkerData}
          title={linkerTitle}
          ariaLabel={ariaLabel}
        >
          {isTop10 && !!content.rank && (
            <RankItemStandard rank={content.rank} />
          )}
          {isLogoChannel ? (
            <ButtonLogo
              as="div"
              logo={
                <ThumborMediaImage
                  url={image.default}
                  dimensions={
                    isLogoChannel
                      ? DIMENSIONS.LOGO_CHANNEL.extraLarge
                      : dimensions
                  }
                  alt={content.altImage}
                  height="auto"
                />
              }
            />
          ) : (
            <>
              {isCreativeMedia && image.default ? (
                <CreativeMediaCard
                  title={title}
                  image={image.default}
                  qualityUserSettings={qualityUserSettings}
                  t={t}
                  isImmersive={false}
                  description={description}
                />
              ) : (
                mediaCard
              )}
            </>
          )}
        </Linker>
      </li>
    );
  }

  // Not clickable card
  return (
    <li
      className={styles.contentRowTemplateItem}
      style={style}
      data-index={index}
    >
      {isLogoChannel ? (
        <ButtonLogo
          as="button"
          logo={
            <ThumborMediaImage
              url={image.default}
              dimensions={
                isLogoChannel ? DIMENSIONS.LOGO_CHANNEL.extraLarge : dimensions
              }
              alt={content.altImage}
              height="auto"
            />
          }
        />
      ) : (
        mediaCard
      )}
    </li>
  );
}
